import { DocumentNode, gql } from '@apollo/client';
import { Box, Button, Stack, StackProps, Text } from '@chakra-ui/react';
import * as React from 'react';

import { event_registration_clicked } from '@/analytics';
import { TicketedEventRegistrationButton } from '@/components/TicketedEventRegistrationButton';
import { EventStatus } from '@/graphql-types/globalTypes';
import { LockIcon, ShieldIcon, StarIcon } from '@/imports/ui/chakra/feather';
import { shouldUseMemberLearningRegistrationCopy } from '@/utilities/memberLearningRegistrationCopy';
import { useFeatureFlags } from '@/imports/ui/hooks/useFeatureFlags';

import { EventStuff_EventFragment } from './graphql-types/EventStuff_EventFragment';

import { BuyTicketsButton } from './components/BuyTicketsButton';
import { DeactivatedMessage } from './components/DeactivatedMessage';
import { EventEndedMessage } from './components/EventEndedMessage';
import { ExternalRegistrationMessage } from './components/ExternalRegistrationMessage';
import { GroupHostedEventMessage } from './components/GroupHostedEventMessage';
import { HappeningNowMessage } from './components/HappeningNowMessage';
import { NoTicketsMessage } from './components/NoTicketsMessage';
import { NotPublishedMessage } from './components/NotPublishedMessage';
import { ReadOnlyRsvpButton } from './components/ReadOnlyRsvpButton';
import { RegisteredMessage } from './components/RegisteredMessage';
import { RsvpMenu } from './components/RsvpMenu';
import { RsvpMessage } from './components/RsvpMessage';
import { SpecialTooltip } from './components/SpecialTooltip';
import { TicketsAvailableMessage } from './components/TicketsAvailableMessage';
import { ViewConferenceButton } from './components/ViewConferenceButton';
import { ViewPurchaseButton } from './components/ViewPurchaseButton';
import { ViewSimpleEventButton } from './components/ViewSimpleEventButton';
import { useEventStuff } from './hooks/useEventStuff';

const EVENT_FRAGMENT = gql`
  fragment EventStuff_EventFragment on EventV2 {
    _id
    ...UseEventStuff_EventFragment
    ...TicketsAvailable_EventFragmet
    ...ReadOnlyRsvpButton_EventFragment
    ...ViewSimpleEvent_EventFragment
    ...TicketedEventRegistrationButton_EventFragment
    ...RsvpMenu_EventFragment
  }
  ${useEventStuff.eventFragment}
  ${TicketsAvailableMessage.eventFragment}
  ${ReadOnlyRsvpButton.eventFragment}
  ${ViewSimpleEventButton.eventFragment}
  ${TicketedEventRegistrationButton.eventFragment}
  ${RsvpMenu.eventFragment}
`;

export const EventStuff: React.FC<{
  event: EventStuff_EventFragment;
  allowSpecialTooltip: boolean;
  allowConferenceJoin: boolean;
}> & {
  eventFragment: DocumentNode;
} = ({ event, allowSpecialTooltip, allowConferenceJoin }) => {
  const isXproConferenceEvent =
    event.__typename === 'SimpleEventV2' && event.isXproConferenceEvent;

  const {
    isEventFuture,
    isEventLive,
    isEventPast,
    isEventTicketed,
    isEventOpen,
    isCurrentUserRegistered,
    didCurrentUserRsvp,
    areTicketsAvailableForCurrentUser,
    hasTickets,
    isExternalRegistrationUrlActive,
    status,
    isAgoraMeetEvent,
    hasValidAgoraMeetSettings,
    isRegistrationBlockedForCurrentUser,
  } = useEventStuff(event);
  const useLearningRegistrationCopy =
    shouldUseMemberLearningRegistrationCopy(event);
  const { hideDateAndTimeForCourses } = useFeatureFlags();
  const hideHappeningNow =
    hideDateAndTimeForCourses && event.isCourseWorkshop === true;

  const message =
    status === EventStatus.DRAFT ? (
      <NotPublishedMessage />
    ) : status === EventStatus.INACTIVE ? (
      <DeactivatedMessage />
    ) : isEventFuture ? (
      isRegistrationBlockedForCurrentUser ? (
        <GroupHostedEventMessage />
      ) : isXproConferenceEvent ? (
        <Text>
          Please visit the Xpro Conference page to register for this event
        </Text>
      ) : isEventOpen ? (
        <RsvpMessage didCurrentUserRsvp={didCurrentUserRsvp} />
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <RegisteredMessage
            useLearningRegistrationCopy={useLearningRegistrationCopy}
          />
        ) : isExternalRegistrationUrlActive ? (
          <ExternalRegistrationMessage />
        ) : hasTickets ? (
          areTicketsAvailableForCurrentUser ? (
            <TicketsAvailableMessage
              event={event}
              useLearningRegistrationCopy={useLearningRegistrationCopy}
            />
          ) : (
            <NoTicketsMessage
              useLearningRegistrationCopy={useLearningRegistrationCopy}
            />
          )
        ) : (
          <NoTicketsMessage
            useLearningRegistrationCopy={useLearningRegistrationCopy}
          />
        )
      ) : null
    ) : isEventLive ? (
      hideHappeningNow ? null : (
        <HappeningNowMessage />
      )
    ) : isEventPast ? (
      <EventEndedMessage />
    ) : null;

  // Show warning for invalid Agora Meet configuration
  const agoraMeetWarning =
    isAgoraMeetEvent && !hasValidAgoraMeetSettings ? (
      <Text color="red.500" fontSize="sm" mt={2}>
        This event has an incomplete Agora Meet configuration. Please contact
        the event organizer.
      </Text>
    ) : null;

  return (
    <Stack
      direction={{
        base: 'column',
        lg: 'row',
      }}
      bg="gray.50"
      borderRadius="lg"
      p="4"
      spacing="4"
      align="center"
      justify="space-between"
    >
      <Stack
        direction={{
          base: 'column',
          lg: 'row',
        }}
        spacing="4"
        align="center"
      >
        {message}
        {agoraMeetWarning}
      </Stack>

      <EventStuffLite
        event={event}
        allowSpecialTooltip={allowSpecialTooltip}
        allowConferenceJoin={allowConferenceJoin}
        direction={{
          base: 'column',
          xl: 'row',
        }}
        alignItems={{ base: 'stretch' }}
        w={{ base: 'full', lg: 'auto' }}
      />
    </Stack>
  );
};

EventStuff.eventFragment = EVENT_FRAGMENT;

export const EventStuffLite: React.FC<
  {
    event: EventStuff_EventFragment;
    allowSpecialTooltip: boolean;
    allowConferenceJoin: boolean;
  } & StackProps
> & {
  eventFragment: DocumentNode;
} = ({ event, allowSpecialTooltip, allowConferenceJoin, ...stackProps }) => {
  const isXproConferenceEvent =
    event.__typename === 'SimpleEventV2' && event.isXproConferenceEvent;

  const {
    isEventFuture,
    isEventLive,
    isEventPast,
    isEventTicketed,
    isEventOpen,
    isCurrentUserRegistered,
    areTicketsAvailableForCurrentUser,
    hasCanonicalVideo,
    hasSessionWithCanonicalVideo,
    isConference,
    canCurrentUserJoinEventEarly,
    isCurrentUserSpeaker,
    status,
    isAtLeastPartiallyOnline,
    isExternalRegistrationUrlActive,
    isAgoraMeetEvent,
    hasValidAgoraMeetSettings,
    isRegistrationBlockedForCurrentUser,
  } = useEventStuff(event);

  if (isXproConferenceEvent) {
    if (isEventPast) {
      return null;
    }

    const xproRedirectBase = process.env.NEXT_PUBLIC_XPRO_REDIRECT_URL_GUEST;

    const registrationUrl =
      (event.isExternalRegistrationUrlActive &&
        event.externalRegistrationUrl) ||
      (xproRedirectBase && event.xproEventId
        ? `${xproRedirectBase}/${event.xproEventId}`
        : null);

    return (
      <Stack {...stackProps}>
        <Button
          colorScheme="blue"
          onClick={() => {
            if (!registrationUrl) return;
            // XPRO user journey: member clicked Registration on an XPro conference
            event_registration_clicked({
              entity_id: event._id,
              entity_type: 'event',
              event_id: event._id,
              event_type: 'xpro_conference',
              xpro_event_id: event.xproEventId ?? undefined,
              registration_provider: 'xpro',
              metadata_destination: registrationUrl,
            });
            window.open(registrationUrl, '_blank', 'noopener,noreferrer');
          }}
          isDisabled={!registrationUrl}
        >
          Registration
        </Button>
      </Stack>
    );
  }

  const hasEventDates = Boolean(event.startDate && event.endDate);
  const useLearningRegistrationCopy =
    shouldUseMemberLearningRegistrationCopy(event);

  const secondary =
    status !== EventStatus.ACTIVE ? null : isEventFuture ? (
      isEventOpen ? (
        isRegistrationBlockedForCurrentUser ? null : (
          <RsvpMenu event={event} />
        )
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={2}
            w={{ base: '100%', md: 'auto' }}
          >
            <ViewPurchaseButton
              eventId={event._id}
              useLearningRegistrationCopy={useLearningRegistrationCopy}
            />
            {hasEventDates ? (
              <BuyTicketsButton
                eventId={event._id}
                eventName={event.name}
                eventStartDate={event.startDate!}
                eventEndDate={event.endDate!}
                isConference={event.__typename === 'ConferenceEventV2'}
                isExternalRegistrationUrlActive={
                  event.isExternalRegistrationUrlActive
                }
                externalRegistrationUrl={event.externalRegistrationUrl}
                useLearningRegistrationCopy={useLearningRegistrationCopy}
              />
            ) : null}
          </Stack>
        ) : isExternalRegistrationUrlActive ||
          areTicketsAvailableForCurrentUser ? (
          <TicketedEventRegistrationButton event={event} />
        ) : null
      ) : null
    ) : isEventLive ? (
      isEventOpen ? (
        isRegistrationBlockedForCurrentUser ? null : (
          <ReadOnlyRsvpButton event={event} />
        )
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={2}
            w={{ base: '100%', md: 'auto' }}
          >
            <ViewPurchaseButton
              eventId={event._id}
              useLearningRegistrationCopy={useLearningRegistrationCopy}
            />
            {hasEventDates ? (
              <BuyTicketsButton
                eventId={event._id}
                eventName={event.name}
                eventStartDate={event.startDate!}
                eventEndDate={event.endDate!}
                isConference={event.__typename === 'ConferenceEventV2'}
                isExternalRegistrationUrlActive={
                  event.isExternalRegistrationUrlActive
                }
                externalRegistrationUrl={event.externalRegistrationUrl}
                useLearningRegistrationCopy={useLearningRegistrationCopy}
              />
            ) : null}
          </Stack>
        ) : isExternalRegistrationUrlActive ||
          areTicketsAvailableForCurrentUser ? (
          <TicketedEventRegistrationButton event={event} />
        ) : null
      ) : null
    ) : isEventPast ? (
      isEventOpen ? null : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <ViewPurchaseButton
            eventId={event._id}
            useLearningRegistrationCopy={useLearningRegistrationCopy}
          />
        ) : (hasCanonicalVideo || hasSessionWithCanonicalVideo) &&
          (isExternalRegistrationUrlActive ||
            areTicketsAvailableForCurrentUser) ? (
          <TicketedEventRegistrationButton
            event={event}
            leftIcon={<LockIcon />}
          >
            Watch Recording{hasSessionWithCanonicalVideo && 's'}
          </TicketedEventRegistrationButton>
        ) : null
      ) : null
    ) : null;

  const primary =
    status !==
    EventStatus.ACTIVE ? null : isEventFuture ? null : isEventLive ? (
      isEventOpen ? (
        isRegistrationBlockedForCurrentUser ? null : isConference ? (
          allowConferenceJoin ? (
            isAtLeastPartiallyOnline && (
              <ViewConferenceButton eventId={event._id} />
            )
          ) : null
        ) : (
          isAtLeastPartiallyOnline && (
            <SpecialTooltip allowSpecialTooltip={allowSpecialTooltip}>
              <ViewSimpleEventButton
                isJoiningWithSpecialPermissions={false}
                event={event}
              />
              {isAgoraMeetEvent && !hasValidAgoraMeetSettings && (
                <Text color="red.500" fontSize="sm">
                  Incomplete Agora Meet configuration
                </Text>
              )}
            </SpecialTooltip>
          )
        )
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          isConference ? (
            allowConferenceJoin ? (
              <ViewConferenceButton eventId={event._id} />
            ) : null
          ) : (
            isAtLeastPartiallyOnline && (
              <SpecialTooltip allowSpecialTooltip={allowSpecialTooltip}>
                <ViewSimpleEventButton
                  isJoiningWithSpecialPermissions={false}
                  event={event}
                />
                {isAgoraMeetEvent && !hasValidAgoraMeetSettings && (
                  <Text color="red.500" fontSize="sm">
                    Incomplete Agora Meet configuration
                  </Text>
                )}
              </SpecialTooltip>
            )
          )
        ) : null
      ) : null
    ) : isEventPast ? (
      isConference ? (
        hasSessionWithCanonicalVideo &&
        (isCurrentUserRegistered ||
          (isEventOpen && !isRegistrationBlockedForCurrentUser)) ? (
          allowConferenceJoin ? (
            <ViewConferenceButton eventId={event._id}>
              Watch Recordings
            </ViewConferenceButton>
          ) : null
        ) : null
      ) : hasCanonicalVideo &&
        (isCurrentUserRegistered ||
          (isEventOpen && !isRegistrationBlockedForCurrentUser)) ? (
        <ViewSimpleEventButton
          isJoiningWithSpecialPermissions={false}
          event={event}
        >
          Watch Recording
        </ViewSimpleEventButton>
      ) : null
    ) : null;

  const special = canCurrentUserJoinEventEarly
    ? isConference
      ? allowConferenceJoin
        ? isAtLeastPartiallyOnline && (
            <ViewConferenceButton
              eventId={event._id}
              colorScheme="gray"
              variant="outline"
            />
          )
        : null
      : isAtLeastPartiallyOnline && (
          <ViewSimpleEventButton
            isJoiningWithSpecialPermissions
            event={event}
            leftIcon={isCurrentUserSpeaker ? <StarIcon /> : <ShieldIcon />}
            colorScheme={
              !isEventLive || !isCurrentUserSpeaker ? 'gray' : undefined
            }
            variant={
              !isEventLive || !isCurrentUserSpeaker ? 'outline' : undefined
            }
          >
            {isEventFuture
              ? 'Preview'
              : isEventPast
              ? 'View Page'
              : !isCurrentUserSpeaker
              ? 'Join as Admin'
              : 'Join Now'}
          </ViewSimpleEventButton>
        )
    : null;

  return (
    <Stack {...stackProps}>
      {secondary}

      {primary ? primary : special}
    </Stack>
  );
};

EventStuffLite.eventFragment = EVENT_FRAGMENT;
